import { Button, toast } from "@devhop/ui";
import { DownloadIcon } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useState } from "react";

async function downloadDataUrl(dataUrl: string, filename: string) {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}

type FormPublicLinkQrBlockProps = {
  href: string;
  formId: string;
  disabled?: boolean;
  title?: string;
  description?: string;
  downloadFilename?: string;
};

export function FormPublicLinkQrBlock({
  href,
  formId,
  disabled = false,
  title = "QR ລິ້ງຟອມ",
  description = "ສະແກນເພື່ອເປີດໜ້າ public · ດາວໂຫຼດ PNG ໄປພິມໄດ້",
  downloadFilename,
}: FormPublicLinkQrBlockProps) {
  const qrFilename =
    downloadFilename ?? `form-${formId}-public-qr.png`;
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!href || disabled) {
      setPreviewSrc(null);
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(href, {
      width: 280,
      margin: 2,
      errorCorrectionLevel: "M",
    }).then((url) => {
      if (!cancelled) setPreviewSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [href, disabled]);

  const handleDownload = async () => {
    if (!href || disabled) return;
    setBusy(true);
    try {
      const dataUrl = await QRCode.toDataURL(href, {
        width: 1024,
        margin: 2,
        errorCorrectionLevel: "M",
      });
      await downloadDataUrl(dataUrl, qrFilename);
      toast.success("ດາວໂຫຼດ QR ແລ້ວ");
    } catch {
      toast.error("ດາວໂຫຼດ QR ບໍ່ສຳເລັດ");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 border-t pt-4">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-muted-foreground text-xs">{description}</p>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div
          className={`mx-auto shrink-0 rounded-lg border bg-white p-3 sm:mx-0 ${disabled ? "opacity-50" : ""}`}
        >
          {previewSrc ? (
            <img
              src={previewSrc}
              alt=""
              className="size-36 sm:size-40"
              aria-hidden
            />
          ) : (
            <div
              className="bg-muted size-36 animate-pulse rounded sm:size-40"
              aria-hidden
            />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit gap-1.5"
            disabled={!href || disabled || busy}
            onClick={() => void handleDownload()}
          >
            <DownloadIcon className="size-4" aria-hidden />
            {busy ? "ກຳລັງດາວໂຫຼດ…" : "ດາວໂຫຼດ QR (PNG)"}
          </Button>
          {disabled ? (
            <p className="text-muted-foreground text-xs">
              Publish ກ່ອນຈຶ່ງໃຊ້ QR ແລະ ລິ້ງ public ໄດ້
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
